import React from 'react';
import styled from 'styled-components';

interface VendorOption {
    branchCodeId: number;
    outlet: string;
    vendorId: string;
    vendorNickname: string;
}

interface Props {
    outletName: string;
    availableVendors: VendorOption[];
    onClose: () => void;
    onConfirm: (selectedVendors: VendorOption[]) => void;
}

export function AddVendorsModal({ outletName, availableVendors, onClose, onConfirm }: Props) {
    const [selected, setSelected] = React.useState<Record<string, boolean>>({});

    const toggle = (vendorId: string) => () => {
        setSelected((prev) => ({ ...prev, [vendorId]: !prev[vendorId] }));
    };

    const allSelected = Object.values(selected).length > 0 && Object.values(selected).every(Boolean);
    const toggleAll = () => {
        if (allSelected) {
            setSelected({});
        } else {
            const next: Record<string, boolean> = {};
            availableVendors.forEach((v) => (next[v.vendorId] = true));
            setSelected(next);
        }
    };

    const handleConfirm = () => {
        const chosen = availableVendors.filter((v) => selected[v.vendorId]);
        onConfirm(chosen);
    };

    return (
        <ModalOverlay>
            <ModalContent>
                <ModalHeader>
                    <h2>Add Vendors to {outletName}</h2>
                    <button className="close-btn" onClick={onClose} type="button">
                        ×
                    </button>
                </ModalHeader>
                <ModalBody>
                    {availableVendors.length === 0 ? (
                        <EmptyState>
                            <p>
                                <strong>No additional vendors available for this outlet</strong>
                            </p>
                        </EmptyState>
                    ) : (
                        <>
                            <VendorsHeader>
                                <div>
                                    <h4>Available Vendors</h4>
                                    <p className="help-text">Select vendors to add to this outlet.</p>
                                </div>
                                <button className="wfp-btn wfp-btn--secondary" onClick={toggleAll} type="button">
                                    {allSelected ? 'Unselect All' : 'Select All'}
                                </button>
                            </VendorsHeader>
                            <VendorsList>
                                {availableVendors.map((vendor) => (
                                    <VendorItem key={vendor.vendorId}>
                                        <label data-selected={!!selected[vendor.vendorId]}>
                                            <input
                                                checked={!!selected[vendor.vendorId]}
                                                onChange={toggle(vendor.vendorId)}
                                                type="checkbox"
                                            />
                                            <span className="vendor-name">{vendor.vendorNickname}</span>
                                            <span className="vendor-id">ID: {vendor.vendorId}</span>
                                        </label>
                                    </VendorItem>
                                ))}
                            </VendorsList>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <button className="wfp-btn wfp-btn--secondary" onClick={onClose} type="button">
                        Cancel
                    </button>
                    <button
                        className="wfp-btn wfp-btn--primary"
                        disabled={availableVendors.length === 0 || !Object.values(selected).some(Boolean)}
                        onClick={handleConfirm}
                        type="button"
                    >
                        Add Selected
                    </button>
                </ModalFooter>
            </ModalContent>
        </ModalOverlay>
    );
}

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e5e5;

    h2 {
        margin: 0;
        color: #333;
        font-size: 1.5rem;
    }

    .close-btn {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: #666;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            color: #333;
        }
    }
`;

const ModalBody = styled.div`
    padding: 1.5rem 2rem;
    overflow-y: auto;
    flex: 1;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 1.5rem 2rem;
    border-top: 1px solid #e5e5e5;

    .wfp-btn {
        padding: 0.75rem 1.5rem;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        font-size: 0.875rem;
        transition: background-color 0.2s;
    }

    .wfp-btn--secondary {
        background: #6c757d;
        color: #fff;
    }
    .wfp-btn--secondary:hover {
        background: #5a6268;
    }

    .wfp-btn--primary {
        background: #007bff;
        color: #fff;
    }
    .wfp-btn--primary:hover:not(:disabled) {
        background: #0056b3;
    }
    .wfp-btn--primary:disabled {
        background: #6c757d;
        color: #fff;
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 2rem 0;
    color: #666;
`;

const VendorsHeader = styled.div`
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;

    h4 {
        margin: 0 0 0.5rem 0;
        color: #333;
        font-size: 1.1rem;
    }

    .help-text {
        margin: 0;
        color: #666;
        font-size: 0.9rem;
    }
`;

const VendorsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const VendorItem = styled.div`
    padding: 0.75rem 1rem;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    background: #f8f9fa;

    label {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        cursor: pointer;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
    }

    .vendor-name {
        font-weight: 500;
        color: #333;
    }

    .vendor-id {
        font-size: 0.85rem;
        color: #666;
        margin-left: auto;
    }

    label[data-selected='true'] {
        background: #007bff;
        color: #fff;
    }
    label[data-selected='true'] .vendor-name,
    label[data-selected='true'] .vendor-id {
        color: #fff;
    }
`;
